/**
 * Язык интерфейса.
 *
 * Показываем английский по умолчанию и русский тем, кто пришёл из региона,
 * где на нём говорят. Исходный язык продукта при этом по-прежнему русский:
 * строки в коде написаны на нём, и непереведённая строка показывает русский
 * текст, а не ключ и не пустоту.
 *
 * Выбор человека сильнее и языка браузера, и страны, и живёт в localStorage:
 * переключатель обязан работать у того, у кого браузер английский, а читать
 * он хочет по-русски.
 */
export type Lang = 'ru' | 'en' | 'es';

export const LANGS: Lang[] = ['ru', 'en', 'es'];

/** Каждый язык назван на себе: «Русский» в английском меню читается как ошибка. */
export const LANG_NAMES: Record<Lang, string> = {
  ru: 'Русский',
  en: 'English',
  es: 'Español',
};

/**
 * Языки, которых ещё нет.
 *
 * Показываем их в переключателе выключенными и с пометкой «скоро», а не
 * молча готовим: список языков — это обещание, и человек должен видеть, на
 * что ему рассчитывать. Выбрать их нельзя намеренно — половина интерфейса на
 * чужом языке хуже, чем его отсутствие.
 */
export const SOON_LANGS: Array<{ code: string; name: string }> = [
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
];

const KEY = 'allswipes-lang';

export function isLang(value: string | null | undefined): value is Lang {
  return value === 'ru' || value === 'en' || value === 'es';
}

/**
 * Языки, с которыми человеку показываем русский интерфейс.
 *
 * Считаем по языкам браузера, а не по стране: язык — это то, что человек
 * выбрал сам, а страна бывает случайной (поездка, VPN, роуминг). Казахский,
 * узбекский, киргизский и таджикский здесь потому, что интерфейса на них у
 * нас пока нет, а русский в этих странах понимают почти все — он ближе, чем
 * английский.
 */
const RUSSIAN_SPEAKING = ['ru', 'uk', 'be', 'kk', 'uz', 'ky', 'tg', 'az', 'hy', 'ka', 'tk', 'mo'];

/**
 * Язык при запуске: сначала адрес (ссылкой на английскую версию можно
 * поделиться), потом сохранённый выбор, потом языки браузера.
 */
export function initialLang(): Lang {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (isLang(fromUrl)) return fromUrl;

    const saved = localStorage.getItem(KEY);
    if (isLang(saved)) return saved;

    const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const raw of prefs) {
      const code = (raw ?? '').toLowerCase().split('-')[0] ?? '';
      if (code === 'en') return 'en';
      if (code === 'es') return 'es';
      if (RUSSIAN_SPEAKING.includes(code)) return 'ru';
    }
    return 'en';
  } catch {
    // Приватный режим: язык просто не переживёт перезагрузку.
    return 'en';
  }
}

export function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* ignore */
  }
}
